import React from 'react';
import Tooth from './Tooth.jsx';
import ToothTooltip from './ToothTooltip.jsx';
import { archPlacements, crownSize } from './geometry.js';
import {
  BRIDGE_BAR, CHART_SHADES, bridgeSpans,
  PERMANENT_ROWS, PRIMARY_ROWS, UPPER_RIGHT, LOWER_RIGHT,
  UPPER_RIGHT_PRIMARY, LOWER_RIGHT_PRIMARY,
  FDI_JUMP_IDLE, asciiDigit, fdiJumpReducer, moveCursor, toothNameKey,
  resolveWithPrimary,
} from '../../lib/teeth.js';

// The odontogram — the whole mouth, on two arches.
//
// ── THE ANATOMICAL FRAME (CORE.md §9) — a patient-safety invariant ──────────
//
// Drawn as the DENTIST LOOKS INTO THE MOUTH: the patient's RIGHT (quadrants 1
// and 4) is on the SCREEN's LEFT, in BOTH locales.
//
// The app is RTL-first and `--dir-flip` is +1 under RTL, so a well-meaning
// "make it RTL-correct" pass is exactly what would mirror the arch and put the
// dentist on the wrong side of the mouth. Three defences: an explicit
// direction:ltr on the <svg>, absolute coordinates only, and permanent
// «راست بیمار»/«چپ بیمار» markers drawn INTO the chart so a mirrored render is
// visible rather than merely wrong. tests/odontogram.test.js pins the order.
//
// ── WHY AN ARCH AND NOT A ROW ──────────────────────────────────────────────
// The curve is not decoration. Each tooth is drawn in a local frame with its
// facial surface toward -y and then ROTATED onto the arch, so buccal keeps
// facing out of the mouth and mesial keeps pointing toward the midline at
// every position — including the third molars, where a flat row would have
// them 90° wrong. Anatomical honesty and legibility come from the same choice.

const PAD = 30;
const LABEL_GAP = 15;

// Per-instance SVG id namespace. SVG ids resolve per DOCUMENT, not per svg:
// with two charts on one page a bare `crownShine` (or `crown-11` clip) would
// resolve to whichever chart rendered first, cross-wiring their defs. NOT
// React.useId — its value is derived from tree POSITION, so two charts at the
// same position in two roots (a portal, a print preview) get the SAME id, and
// its delimiters (`:`/`«»`) are not safe inside a `url(#…)` reference. A
// module counter is unique across roots by construction. No SSR here (Parcel
// SPA), so nothing needs to match a server render.
let chartSeq = 0;

// Visually hidden but still announced — the standard clip pattern. display:none
// (or aria-hidden) would silence the live region entirely.
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
  border: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
};

// Upper arch bows up, lower bows down; the two are drawn as facing "∩" / "∪"
// with a gap between, exactly as a printed chart does.
const ARCH = {
  permanent: { rx: 232, ry: 96 },
  primary: { rx: 150, ry: 62 },
};

function Arch({
  teeth, states, selected, highlight, treated, hovered, onSelect, onSurfaceSelect,
  onHover, onHoverEnd, labelMode, cx, cy, rx, ry, upper, t, idPrefix,
}) {
  return (
    <>
      {/* This map IS the DRAWING order, screen left → right — the same order
          the keyboard cursor traverses (the svg is ONE tab stop; arrow keys
          walk `moveCursor` over these rows). Traversal deliberately IGNORES
          RTL: the arch is anatomical, not textual (CORE.md §9), and reversing
          it under fa would walk the mouth backwards relative to the drawn
          chart. This is the one place ignoring the locale is correct. */}
      {archPlacements(teeth, { cx, cy, rx, ry, upper }).map(({ fdi, x, y, rotation, scale }) => {
        const state = states[fdi];
        const { w, h } = crownSize(fdi);
        // The number sits OUTSIDE the arch, along the same outward normal the
        // tooth is rotated onto, so it never lands on a clickable surface.
        //
        // Offset by the crown's HALF-DIAGONAL, not half its height: the tooth
        // is rotated, so its corner — not its facial edge — is the nearest
        // point to the label. Using h/2 let a molar's corner slide under its
        // own number, and under the smaller Universal number below it.
        const labelR = Math.hypot(w, h) / 2 + LABEL_GAP;
        const rad = ((rotation - 90) * Math.PI) / 180;
        const lx = x + Math.cos(rad) * labelR;
        const ly = y + Math.sin(rad) * labelR;
        const isSel = selected === fdi;

        return (
          <g key={fdi}>
            {/* scale comes from archPlacements: when a row cannot fit the arch
                at full size it shrinks the crowns TOO, not just the gaps —
                shrinking only the spacing would reintroduce the overlap. It is
                1 for every real chart size; this is the defensive path. */}
            <g transform={`translate(${x}, ${y}) rotate(${rotation})${scale < 1 ? ` scale(${scale})` : ''}`}>
              <Tooth
                fdi={fdi}
                state={state}
                selected={isSel}
                highlighted={!!highlight?.has(fdi)}
                treatedCount={treated?.get(fdi)?.count || 0}
                hovered={hovered === fdi}
                onSelect={onSelect}
                onSurfaceSelect={onSurfaceSelect}
                onHover={onHover}
                onHoverEnd={onHoverEnd}
                t={t}
                idPrefix={idPrefix}
              />
            </g>
            {/* Labels are NOT rotated: a number readable only by tilting your
                head is a number nobody reads. */}
            <text
              x={lx} y={ly}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={9.5}
              fontWeight={isSel ? 800 : 600}
              fill={isSel ? '#0EA5A0' : '#64748b'}
              style={{ userSelect: 'none', pointerEvents: 'none' }}
            >
              {fdi}
            </text>
            {labelMode === 'both' && state?.universal && (
              <text
                x={lx} y={ly + 10}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={7.5} fill="#a8b3c2"
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {state.universal}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}

export default function Odontogram({
  teeth = [],
  selected = null,
  /**
   * FDI numbers this screen wants marked, as a Set.
   *
   * The visit workspace passes the teeth a طرح درمان touches (dent-visit.md
   * §3.1.3, "link treatments directly to specific teeth on the Odontogram").
   * It is an ANNOTATION and not clinical state: nothing about it is stored on
   * the tooth, and the chart draws it as a dashed ring precisely so it cannot
   * be mistaken for a recorded finding.
   *
   * A Set rather than an array because this is a per-tooth membership test
   * inside the render of 52 teeth.
   */
  highlight = null,
  /**
   * FDI numbers work has been RECORDED on — «درمان‌شده».
   *
   * `Map<fdi, { count, lastAtMs, names[] }>`, built by `treatedByFdi` in
   * Visit.shared.js from the patient's treatment records. The chart draws a
   * check badge, and the tooltip reads the same map rather than fetching per
   * hover — 52 teeth and a hover read would be 52 possible round trips.
   *
   * An ANNOTATION, like `highlight`: nothing about it is stored on the tooth.
   * The two are independent and routinely both apply — a tooth can have a
   * finished restoration and another treatment still planned.
   */
  treated = null,
  /**
   * What is PLANNED per tooth — `Map<fdi, [{ status, description, … }]>` from
   * `planTeethIndex`. Only the tooltip reads it: the arch already says
   * "planned" with the dashed ring, and the card is where the dentist finds
   * out WHAT is planned without opening anything.
   */
  planned = null,
  onSelect,
  onSurfaceSelect,
  showPrimary = 'auto',
  labelMode = 'fdi',
  t,
  formatDate,
}) {
  const wrapRef = React.useRef(null);
  const svgRef = React.useRef(null);
  // Every id this chart defines — the gradient, the blur filter, the
  // per-tooth clips — is namespaced by this per-instance prefix (see chartSeq
  // above). Lazy state, not a bare expression: it must be minted once per
  // MOUNT, not once per render.
  const [idPrefix] = React.useState(() => `odo${(chartSeq += 1)}-`);
  const [hover, setHover] = React.useState(null);   // { fdi, x, y }
  // The keyboard cursor — the FDI the arrow keys sit on. Kept across blur so
  // tabbing away and back resumes where the dentist left off.
  const [cursor, setCursor] = React.useState(null);
  // The FDI digit buffer. A ref, not state: a half-typed pair renders nothing.
  const fdiBufRef = React.useRef(FDI_JUMP_IDLE);

  const handleHover = React.useCallback((fdi, { clientX, clientY }) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    setHover({ fdi, x: clientX - box.left, y: clientY - box.top });
  }, []);

  // Only clear when leaving the tooth the card is FOR: moving between adjacent
  // teeth fires the new tooth's enter before the old one's leave, and an
  // unconditional clear would blank the card that just opened.
  const handleHoverEnd = React.useCallback((fdi) => {
    setHover((h) => (h && h.fdi !== fdi ? h : null));
  }, []);

  const states = React.useMemo(() => {
    const map = {};
    for (const s of teeth) map[s.toothFdi] = s;
    return map;
  }, [teeth]);

  // The rule lives in lib/teeth.js because the MOBILE tooth picker offers the
  // same set of teeth: a picker listing 55-51 over a chart not drawing them
  // would be two controls describing different mouths.
  const withPrimary = React.useMemo(
    () => resolveWithPrimary(showPrimary, teeth),
    [showPrimary, teeth],
  );

  const W = 620;
  const cx = W / 2;
  const { rx, ry } = ARCH.permanent;
  const P = ARCH.primary;

  // Upper arch apex near the top, lower arch apex near the bottom, mirrored
  // about a midline gap — the layout of every printed odontogram.
  const upperBase = PAD + ry + 26;
  const lowerBase = upperBase + 96;
  const H = lowerBase + ry + PAD + 22;

  const common = {
    states, selected, highlight, treated, hovered: hover?.fdi, onSelect, onSurfaceSelect,
    onHover: handleHover, onHoverEnd: handleHoverEnd, labelMode, t, idPrefix,
  };

  // The visible rows with their arch options, in VERTICAL screen order —
  // primary arches nest between the permanent ones. ONE definition feeds the
  // bridge bars, the keyboard rows and the world-position lookup, so they can
  // never disagree about what is on screen or where.
  const rowDefs = React.useMemo(() => [
    { row: PERMANENT_ROWS.upper, opts: { cx, cy: upperBase, rx, ry, upper: true } },
    ...(withPrimary ? [
      { row: PRIMARY_ROWS.upper, opts: { cx, cy: upperBase + 14, rx: P.rx, ry: P.ry, upper: true } },
      { row: PRIMARY_ROWS.lower, opts: { cx, cy: lowerBase - 14, rx: P.rx, ry: P.ry, upper: false } },
    ] : []),
    { row: PERMANENT_ROWS.lower, opts: { cx, cy: lowerBase, rx, ry, upper: false } },
    // cx/bases/radii are module-level constants folded into render locals —
    // only withPrimary can actually change between renders.
  ], [withPrimary, cx, upperBase, lowerBase, rx, ry, P.rx, P.ry]);

  // fdi → its world (viewBox) position.
  //
  // Computed ONCE from the same archPlacements() the teeth are drawn with, and
  // shared by the keyboard cursor's hover card and the bridge bars. Spacing is
  // arc-length based and depends on the WHOLE row (each tooth takes room
  // proportional to its crown), so a per-index re-derivation would not even be
  // reproducible — it would put the cursor and the bridge bars on coordinates
  // no tooth occupies.
  const worldPos = React.useMemo(() => {
    const map = {};
    for (const { row, opts } of rowDefs) {
      for (const p of archPlacements(row, opts)) map[p.fdi] = p;
    }
    return map;
  }, [rowDefs]);

  // بریج bars — one per span, computed per ROW because upper and lower are
  // separate rowOrders (bridgeSpans can then never leak a span across the
  // midline gap between the arches).
  const bridgeBars = React.useMemo(() => {
    return rowDefs.flatMap(({ row, opts }) => bridgeSpans(states, row)
      // A one-tooth span (a lone abutment, a bridge half-charted) is real
      // data, but a bar over a single tooth connects nothing — skip drawing.
      .filter((span) => span.teeth.length > 1)
      .map((span) => ({
        key: span.teeth.join('-'),
        // The chord through the teeth's world positions follows the arch
        // closely enough at bridge lengths; pontic (usually missing) teeth
        // are on the polyline like any other, so the bar crosses the gap.
        points: span.teeth
          .map((fdi) => worldPos[fdi])
          .filter(Boolean)
          .map((p) => `${p.x},${p.y}`)
          .join(' '),
      })));
  }, [states, rowDefs, worldPos]);

  // Move the keyboard cursor to a tooth: the halo and the hover card are the
  // SAME mechanism the mouse uses — the tooth's world position is mapped back
  // to client coordinates and fed through handleHover, so the two paths
  // cannot drift apart visually.
  const focusTooth = React.useCallback((fdi) => {
    setCursor(fdi);
    const p = worldPos[fdi];
    const rect = svgRef.current?.getBoundingClientRect();
    if (!p || !rect) return;
    // The svg scales uniformly from its viewBox (width:100%), so world →
    // client is one factor. jsdom reports a zero rect; the fallback keeps the
    // card maths finite there.
    const scale = rect.width ? rect.width / W : 1;
    handleHover(fdi, {
      clientX: rect.left + p.x * scale,
      clientY: rect.top + p.y * scale,
    });
  }, [worldPos, handleHover]);

  // Keyboard charting. The svg is ONE tab stop (Tab passes straight through —
  // no focus trap); arrows move the cursor, digits jump to an FDI, Enter or
  // Space opens the tooth. Arrow traversal follows DRAWING order and
  // deliberately IGNORES RTL (CORE.md §9): ArrowRight is always toward the
  // SCREEN's right, in both locales — the arch is anatomical, not textual.
  const handleKeyDown = (e) => {
    const rows = rowDefs.map((d) => d.row);
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight'
        || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();   // the page must not scroll under the chart
      focusTooth(moveCursor(rows, cursor, e.key));
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && cursor) {
      e.preventDefault();
      onSelect?.(cursor);   // the same path a click takes
      return;
    }
    if (e.key === 'Escape') {
      fdiBufRef.current = fdiJumpReducer(fdiBufRef.current, { type: 'clear' });
      return;
    }
    if (asciiDigit(e.key) != null) {
      e.preventDefault();
      fdiBufRef.current = fdiJumpReducer(fdiBufRef.current, {
        type: 'digit', digit: e.key, now: Date.now(), teeth: rows.flat(),
      });
      if (fdiBufRef.current.jump) focusTooth(fdiBufRef.current.jump);
    }
  };

  const handleChartBlur = () => {
    fdiBufRef.current = fdiJumpReducer(fdiBufRef.current, { type: 'clear' });
    // Close the card only if it is the CURSOR's — a mouse hovering elsewhere
    // keeps its own card (same rule handleHoverEnd applies between teeth).
    if (cursor) handleHoverEnd(cursor);
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative' }}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="group"
        aria-label={t('chart.aria.odontogram')}
        // ONE tab stop for the whole chart — arrow keys navigate inside, Tab
        // leaves normally. The teeth themselves are deliberately not focusable
        // (52 tab stops was a keyboard trap in practice, see Tooth.jsx).
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onBlur={handleChartBlur}
        // direction:ltr is LOAD-BEARING — see the header. Do not remove it to
        // "fix RTL": the arch is anatomical, not textual.
        style={{ direction: 'ltr', maxWidth: 760, display: 'block', margin: '0 auto' }}
      >
        <defs>
          {/* ONE gradient for all 52 crowns. objectBoundingBox units (the
              default) fit it to each referencing path's own box, so a molar
              and an incisor both get a soft white centre falling off to a
              cool rim — without 52 duplicated defs. Ghosts never reference
              it; missing teeth stay flat grey. The id is prefixed per chart
              INSTANCE — see idPrefix above. */}
          <radialGradient id={`${idPrefix}crownShine`} cx="50%" cy="42%" r="68%">
            <stop offset="0%" stopColor={CHART_SHADES.crownCentre} />
            <stop offset="55%" stopColor={CHART_SHADES.crownCentre} />
            <stop offset="100%" stopColor={CHART_SHADES.crownRim} />
          </radialGradient>
          {/* Widened filter region so the blur is not clipped at the box. */}
          <filter id={`${idPrefix}archShadowBlur`} x="-20%" y="-40%" width="140%" height="180%">
            <feGaussianBlur stdDeviation="7" />
          </filter>
        </defs>

        {/* A very soft shadow under each arch — one blurred ellipse per arch,
            drawn first (= behind everything). Depth only, no meaning. */}
        <g pointerEvents="none" opacity={0.12} filter={`url(#${idPrefix}archShadowBlur)`}>
          <ellipse cx={cx} cy={upperBase - ry * 0.45} rx={rx} ry={ry * 0.75}
                   fill={CHART_SHADES.archShadow} />
          <ellipse cx={cx} cy={lowerBase + ry * 0.45} rx={rx} ry={ry * 0.75}
                   fill={CHART_SHADES.archShadow} />
        </g>

        <line
          x1={cx} y1={PAD - 14} x2={cx} y2={H - PAD + 6}
          stroke="#e8edf3" strokeWidth={1} strokeDasharray="4 4"
        />

        {/* بریج bars, BEHIND the teeth: the bar reads as connecting the
            crowns while ghosts, glyphs and strike-throughs stay legible
            above it. Crossing missing pontic positions is the point. The
            stroke recipe is BRIDGE_BAR (teeth.js) — the ONE definition the
            legend swatch also spreads, so the two cannot drift. */}
        {bridgeBars.map((b) => (
          <polyline
            key={b.key}
            points={b.points}
            fill="none"
            {...BRIDGE_BAR}
            pointerEvents="none"
          />
        ))}

        {/* Side markers, permanent and INSIDE the SVG: if the arch is ever
            mirrored these move with it, so the error is visible. */}
        <text x={PAD - 12} y={PAD - 12} fontSize={10.5} fontWeight={700} fill="#94a3b8">
          {t('chart.patientRight')}
        </text>
        <text x={W - PAD + 12} y={PAD - 12} textAnchor="end"
              fontSize={10.5} fontWeight={700} fill="#94a3b8">
          {t('chart.patientLeft')}
        </text>

        <Arch {...common} teeth={PERMANENT_ROWS.upper}
              cx={cx} cy={upperBase} rx={rx} ry={ry} upper />
        <Arch {...common} teeth={PERMANENT_ROWS.lower}
              cx={cx} cy={lowerBase} rx={rx} ry={ry} upper={false} />

        {/* Primary teeth nest INSIDE the permanent arches — which is where
            they are in a mixed dentition. */}
        {withPrimary && (
          <>
            <Arch {...common} teeth={PRIMARY_ROWS.upper}
                  cx={cx} cy={upperBase + 14} rx={P.rx} ry={P.ry} upper />
            <Arch {...common} teeth={PRIMARY_ROWS.lower}
                  cx={cx} cy={lowerBase - 14} rx={P.rx} ry={P.ry} upper={false} />
          </>
        )}
      </svg>

      {/* Announce the keyboard cursor — «دندان ۳۶ — مولر اول». A visually
          hidden HTML live region, not SVG <text>: it is speech, not chart,
          and it must never affect the drawing or its hit areas. */}
      <div aria-live="polite" role="status" style={SR_ONLY}>
        {cursor ? `${t('chart.tooth')} ${cursor} — ${t(toothNameKey(cursor))}` : ''}
      </div>

      {hover && (
        <ToothTooltip
          fdi={hover.fdi}
          state={states[hover.fdi]}
          x={hover.x}
          y={hover.y}
          containerWidth={wrapRef.current?.clientWidth}
          treated={treated?.get(hover.fdi) || null}
          planned={planned?.get(hover.fdi) || null}
          t={t}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

export { UPPER_RIGHT, LOWER_RIGHT, UPPER_RIGHT_PRIMARY, LOWER_RIGHT_PRIMARY };
